import * as React from 'react';
import { useEffect, useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import Table from '../../components/Table';
import { ITableColumns } from '../../models';
import { TaskDecision, TaskStatus } from '@tff/types/tff';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { apigeeEndpoint } from '../../apis';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { LoadingContext } from '../../contexts/LoadingContextProvider';
import { LegacyAmendFailureUpdateRequest } from '@tff/types/tff/amend-failure-service';
import { keycloak } from '../../keycloak';
import { MUIDataTableMeta, MUIDataTableOptions } from 'mui-datatables';
import { EnhancedAmendFailureDynamoItem } from './amend-failures';
import { useMessages } from '../../hooks/use-messages';
import { Link } from 'react-router-dom';
import ActionsToolbar from './ActionsToolbar';
import TFFDialog from '../../styled-components/TFFDialog';
import TFFTypography from '../../styled-components/TFFTypography';
import { Box } from '@mui/material';

interface IAmendFailureTableProps {
  data: EnhancedAmendFailureDynamoItem[];
}

const amendFailureAction = yup.object().shape({
  status: yup.string().required(),
});

const updateAmendFailure = async (data: LegacyAmendFailureUpdateRequest) => {
  const response = await apigeeEndpoint.post('amend-failure', data);
  return response.data;
};

const AmendFailuresTable: React.FC<IAmendFailureTableProps> = ({ data }) => {
  const intl = useIntl();

  const [amendFailureToEdit, setAmendFailureToEdit] = useState<EnhancedAmendFailureDynamoItem | undefined>(undefined);
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [openCloseDialog, setOpenCloseDialog] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showLoading, closeLoading } = useContext(LoadingContext);
  const { addMessage } = useMessages();

  const { reset } = useForm<ChangeFormValues>({ resolver: yupResolver(amendFailureAction) });

  const openActionDialogWithTask = (dataIndex: number) => {
    reset();
    const amendFailureToEdit = data[dataIndex];
    setAmendFailureToEdit(amendFailureToEdit);
  };

  const columnsData: ITableColumns<EnhancedAmendFailureDynamoItem>[] = [
    {
      name: 'failureType',
      label: intl.formatMessage({ id: 'global.failureType' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'recordLocator',
      label: intl.formatMessage({ id: 'global.recordLocator' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
        customBodyRender: (value: string | number, tableMeta: MUIDataTableMeta) => {
          return <Link to={`/tff/order/${value}@${tableMeta.rowData[9]}`}>{value}</Link>;
        },
      },
    },
    {
      name: 'clientPnr',
      label: intl.formatMessage({ id: 'global.clientPnr' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'bookingSource',
      label: intl.formatMessage({ id: 'global.bookingSource' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'product',
      label: intl.formatMessage({ id: 'global.product' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'origin',
      label: intl.formatMessage({ id: 'global.origin' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'destination',
      label: intl.formatMessage({ id: 'global.destination' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'departureTime',
      label: intl.formatMessage({ id: 'global.departureTime' }), // TimeToLive
      options: {
        customBodyRender: value => <p style={{ margin: 0 }}>{moment(value).format('DD.MM.YYYY - HH:mm')}</p>,
        display: true,
        filter: false,
        filterList: [],
      },
    },
    {
      name: 'returnTime',
      label: intl.formatMessage({ id: 'global.returnTime' }),
      options: {
        customBodyRender: value => <p style={{ margin: 0 }}>{moment(value).format('DD.MM.YYYY - HH:mm')}</p>,
        display: true,
        filter: false,
        filterList: [],
      },
    },
    {
      name: 'carrierCode',
      label: intl.formatMessage({ id: 'global.carrierCode' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'status',
      label: intl.formatMessage({ id: 'global.status' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
        filterOptions: {
          names: ['CLOSE', 'NEW'],
        },
      },
    },
    {
      name: 'description',
      label: intl.formatMessage({ id: 'global.description' }),
      options: {
        display: true,
        filter: true,
        filterList: [],
      },
    },
    {
      name: 'updatedAt',
      label: intl.formatMessage({ id: 'global.updatedAt' }),
      options: {
        customBodyRender: value => <p style={{ margin: 0 }}>{moment(value).format('DD.MM.YYYY - HH:mm')}</p>,
        display: true,
        filter: false,
        filterList: [],
      },
    },
    {
      name: 'createdAt',
      label: intl.formatMessage({ id: 'global.createdAt' }),
      options: {
        customBodyRender: value => <p style={{ margin: 0 }}>{moment(value).format('DD.MM.YYYY - HH:mm')}</p>,
        display: true,
        filter: false,
        filterList: [],
      },
    },
  ];

  const { mutate, isLoading } = useMutation(updateAmendFailure, {
    onSuccess: data => {
      if (data?.errorMessage) {
        toast.error(data.errorMessage);
      } else {
        toast.success(intl.formatMessage({ id: 'global.dataSuccessfullySend' }));
      }
    },
    onError: (err: Error) => {
      toast.error(err.message.toString());
    },
    onSettled: () => {
      queryClient.invalidateQueries('amendFailures');
    },
  });

  useEffect(() => {
    isLoading ? showLoading('Loading Amend-Failures') : closeLoading();
  }, [isLoading]);

  type ChangeFormValues = {
    status: TaskStatus;
    failureType: TaskDecision;
    remindDate: Date;
    remark: string;
  };

  const handleConfirmAmendFailureDialog = (type: 'CLOSE' | 'IN_PROGRESS') => {
    if (amendFailureToEdit) {
      const action = type === 'CLOSE' ? 'REMOVE_AMEND_ERROR_FROM_LIST' : 'SET_AMEND_FAILURE_TO_IN_PROGRESS';
      const updatedAmendFailure = { ...amendFailureToEdit, status: type, action: undefined };
      setAmendFailureToEdit(updatedAmendFailure);

      const updateAmendFailureRequest: LegacyAmendFailureUpdateRequest = {
        amendFailureToCreateOrUpdate: updatedAmendFailure,
        updateRemark: type === 'CLOSE' ? 'Manually removed from list' : 'Manually set to in progress',
        updatedBy: keycloak?.profile?.username,
      };

      mutate(updateAmendFailureRequest, {
        onSuccess: data => {
          const status = data?.errorMessage ? 'Unsuccessful' : 'Successful';
          // Use addMessage with the specific amendFailure recordLocator and bookingSource
          addMessage({
            createdBy: keycloak?.profile?.email ?? 'UNKNOWN',
            recordLocator: amendFailureToEdit.recordLocator,
            type: 'NOTICE',
            source: amendFailureToEdit.bookingSource as string,
            action,
            status,
          });
        },
        onError: () => {
          addMessage({
            createdBy: keycloak?.profile?.email ?? 'UNKNOWN',
            recordLocator: amendFailureToEdit.recordLocator,
            type: 'NOTICE',
            source: amendFailureToEdit.bookingSource as string,
            action,
            status: 'Unsuccessful',
          });
        },
      });
      setAmendFailureToEdit(undefined);
    }
  };

  const tableOptions: MUIDataTableOptions = {
    selectableRows: 'single',
    rowsPerPage: 50,
    customToolbarSelect: selectedRows => {
      return (
        <ActionsToolbar
          selectedRow={selectedRows.data.map(d => d.dataIndex)[0]}
          handleSetInProgress={handleSetInProgress}
          handleRemoveFromAmendFailure={handleRemoveFromAmendFailure}
        />
      );
    },
  };

  const handleSetInProgress = (selectedRow: number) => {
    openActionDialogWithTask(selectedRow);
    setOpenProgressDialog(true);
  };

  const handleRemoveFromAmendFailure = (selectedRow: number) => {
    openActionDialogWithTask(selectedRow);
    setOpenCloseDialog(true);
  };

  return (
    <>
      {openProgressDialog && amendFailureToEdit && (
        <TFFDialog
          dialogId={'set-amend-failure-in-progress'}
          header={<TFFTypography variant="lead" size="large" fontWeight="semibold" text="Set booking in progress" />}
          isOpen={openProgressDialog}
          confirmButton
          cancelButton
          onCancel={() => {
            setOpenProgressDialog(false);
          }}
          onConfirm={() => {
            handleConfirmAmendFailureDialog('IN_PROGRESS');
          }}
        >
          <TFFTypography
            text={`Do you want to set the status of the booking "${amendFailureToEdit.recordLocator}" to 'In progress'?`}
          />
        </TFFDialog>
      )}

      {openCloseDialog && amendFailureToEdit && (
        <TFFDialog
          dialogId={'close-amend-failure'}
          header={<TFFTypography variant="lead" size="large" fontWeight="semibold" text="Close Amend-Failure" />}
          isOpen={openCloseDialog}
          confirmButton
          cancelButton
          onCancel={() => {
            setOpenCloseDialog(false);
          }}
          onConfirm={() => {
            handleConfirmAmendFailureDialog('CLOSE');
          }}
        >
          <TFFTypography
            text={`Do you want to close and remove the Amend-failure for booking "${amendFailureToEdit.recordLocator}" from list?`}
          />
        </TFFDialog>
      )}

      <Box
        sx={{
          '& .MuiTableCell-body:first-child': {
            backgroundColor: '#ffffff',
          },
        }}
      >
        <Table
          data={data && Array.isArray(data) && data.length > 0 ? data : []}
          columnsData={columnsData}
          tableId="amendFailureTable"
          tableName="Amend-Failure Table"
          otherOptions={tableOptions}
        />
      </Box>
    </>
  );
};

export default AmendFailuresTable;
